import { Injectable, OnInit } from '@angular/core';
import { CookiesService } from '../cookies/cookies.service';
import { Observable, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SessionService implements OnInit {
  private static readonly SESSION_KEY = 'sessionId';
  private static readonly EXPIRATION_KEY = 'sessionExpiration';

  private readonly expirationDuration = 30 * 60 * 1000; // 30 minutes in milliseconds
  private _sessionId: string;

  constructor(
    private readonly cookiesService: CookiesService
  ) {
  }

  ngOnInit(): void {
  }

  /**
   * Initializes the session by checking expiration and generating a new session ID if necessary.
   */

  private initializeSession(): Observable<string> {
    const existingSessionId = sessionStorage.getItem(
      SessionService.SESSION_KEY
    );
    const expiration = sessionStorage.getItem(SessionService.EXPIRATION_KEY);

    return this.cookiesService.consentGranted$
      .pipe(
        switchMap((consent) => {
          if (!!consent) {
            if (existingSessionId && expiration && !this.isExpired(+expiration)) {
              this._sessionId = existingSessionId;
            } else {
              this._sessionId = this.generateSessionId();
              this.setSession(this._sessionId);
            }

            return of(this._sessionId);
          }

          return of(null)
        }));
  }

  /**
   * Retrieves the session ID.
   * @returns The session ID.
   */
  getSessionId(): Observable<string> {
    if (!!this._sessionId) {
      return of(this._sessionId);
    }
    else return this.initializeSession();
  }

  /**
   * Generates a unique session ID.
   * @returns A new session ID.
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Checks if the session ID has expired.
   * @param expirationTimestamp The expiration timestamp to check.
   * @returns True if expired, false otherwise.
   */
  private isExpired(expirationTimestamp: number): boolean {
    return Date.now() > expirationTimestamp;
  }

  /**
   * Sets the session ID and its expiration timestamp in session storage.
   * @param sessionId The session ID to store.
   */
  private setSession(sessionId: string): void {
    const expirationTimestamp = Date.now() + this.expirationDuration;
    sessionStorage.setItem(SessionService.SESSION_KEY, sessionId);
    sessionStorage.setItem(
      SessionService.EXPIRATION_KEY,
      expirationTimestamp.toString()
    );
  }
}
